import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  message,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Divider,
  Space,
  Radio,
  ConfigProvider,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/zh-cn";
import locale from "antd/locale/zh_CN";
import { CustomerService } from "../services/customer.service";
import { productsService } from "../services/products.service";
import { regionsService } from "../services/regions.service";
import { enumService } from "../services/enum.service";

dayjs.locale("zh-cn");

const { Option } = Select;

interface ElderFormData {
  name: string;
  gender: number;
  age: number;
  birthday: Dayjs;
  identity: number;
}

interface CreateCustomerFormData {
  // 客户信息
  customerName: string;
  customerGender: number;
  customerPhone: string;

  // 地址信息
  provinceId: number;
  cityId: number;
  detailAddress: string;
  latitude: number;
  longitude: number;

  // 老人信息
  elderPhone: string;
  elderCount: number;
  elders: ElderFormData[];

  // 订单信息
  productId: number;
}

interface Province {
  id: number;
  name: string;
}

interface City {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  priceFen: number;
  elderCount?: number; // 老人数量
}

interface EnumOption {
  value: number;
  label: string;
}

const CreateCustomer: React.FC = () => {
  const [form] = Form.useForm<CreateCustomerFormData>();
  const [loading, setLoading] = useState(false);
  const [elderCount, setElderCount] = useState<number>(1);

  // 下拉选项数据
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // 所有产品列表
  const [genderOptions, setGenderOptions] = useState<EnumOption[]>([]);
  const [elderIdentityOptions, setElderIdentityOptions] = useState<
    EnumOption[]
  >([]);

  /**
   * 根据老人数量过滤产品列表
   * @param productList 产品列表
   * @param count 老人数量
   * @returns 过滤后的产品列表
   */
  const filterProductsByElderCount = (
    productList: Product[],
    count: number
  ): Product[] => {
    return productList.filter(
      (product) =>
        product.elderCount === count || product.elderCount === undefined
    );
  };

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      // 并行加载所有下拉选项数据
      const [provincesData, productsData, genderData, elderIdentityData] =
        await Promise.all([
          regionsService.getProvinces(),
          productsService.getBasicServiceProducts(),
          enumService.getGenderOptions(),
          enumService.getElderIdentityOptions(),
        ]);

      setProvinces(provincesData);
      setAllProducts(productsData); // 保存所有产品
      setProducts(filterProductsByElderCount(productsData, 1)); // 默认过滤1位老人的产品
      setGenderOptions(genderData);
      setElderIdentityOptions(elderIdentityData);
    } catch (error) {
      console.error("加载初始数据失败:", error);
      message.error("加载初始数据失败，请刷新页面重试");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载初始数据
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // 处理省份变化，加载对应城市
  const handleProvinceChange = async (provinceId: number) => {
    try {
      form.setFieldsValue({ cityId: undefined });
      setCities([]);

      const citiesData = await regionsService.getCitiesByProvinceId(provinceId);
      setCities(citiesData);
    } catch (error) {
      console.error("加载城市数据失败:", error);
      message.error("加载城市数据失败");
    }
  };

  // 处理老人数量变化
  const handleElderCountChange = (value: number) => {
    setElderCount(value);

    // 根据老人数量过滤产品列表
    const filteredProducts = filterProductsByElderCount(allProducts, value);
    setProducts(filteredProducts);

    // 清空当前选择的产品，因为可能不符合新的老人数量
    form.setFieldsValue({ productId: undefined });

    // 更新表单中的老人信息数组
    const currentElders = form.getFieldValue("elders") || [];
    const newElders = Array.from(
      { length: value },
      (_, index) =>
        currentElders[index] || {
          name: "",
          gender: 1, // 默认男
          age: undefined,
          birthday: undefined,
          identity: undefined,
        }
    );
    form.setFieldsValue({ elders: newElders });
  };

  // 根据老人身份自动设置性别
  const handleElderIdentityChange = (identity: number, elderIndex: number) => {
    let gender: number;

    // 根据身份判断性别
    switch (identity) {
      case 1: // 父亲
      case 3: // 公公
      case 5: // 岳父
        gender = 1; // 男
        break;
      case 2: // 母亲
      case 4: // 婆婆
      case 6: // 岳母
        gender = 2; // 女
        break;
      case 7: // 其它
      default:
        gender = form.getFieldValue(["elders", elderIndex, "gender"]) || 1; // 保持当前值或默认男
        return; // 其它关系不自动设置性别
    }

    // 自动设置性别
    form.setFields([
      {
        name: ["elders", elderIndex, "gender"],
        value: gender,
      },
    ]);
  };

  // 处理生日变化，自动计算年龄
  const handleBirthdayChange = (date: Dayjs | null, elderIndex: number) => {
    if (date) {
      const age = dayjs().diff(date, "year");

      // 使用 setFields 来同时更新生日和年龄字段
      form.setFields([
        {
          name: ["elders", elderIndex, "birthday"],
          value: date,
        },
        {
          name: ["elders", elderIndex, "age"],
          value: age,
        },
      ]);
    }
  };

  // 提交表单
  const handleSubmit = async (values: CreateCustomerFormData) => {
    try {
      setLoading(true);

      // 构建提交数据，将 Dayjs 对象转换为 ISO 字符串
      const submitData = {
        customerName: values.customerName,
        customerGender: values.customerGender,
        customerPhone: values.customerPhone,
        cityId: values.cityId,
        detailAddress: values.detailAddress,
        latitude: values.latitude,
        longitude: values.longitude,
        elderPhone: values.elderPhone,
        elderCount: values.elderCount,
        elders: values.elders.map((elder) => ({
          ...elder,
          birthday:
            elder.birthday instanceof dayjs
              ? elder.birthday.toISOString()
              : dayjs(elder.birthday).toISOString(),
        })),
        productId: values.productId,
      };

      const result = await CustomerService.createCustomerOrder(submitData);

      message.success(
        `客户创建成功！客户ID: ${result.customerId}, 订单ID: ${result.orderId}, 订单金额: ¥${result.orderAmountYuan}`
      );

      // 重置表单
      form.resetFields();
      setElderCount(1);
      setCities([]);
    } catch (error: any) {
      console.error("创建客户失败:", error);
      message.error(error.message || "创建客户失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider locale={locale}>
      <div style={{ margin: "0 auto" }}>
        <Card title="后台创建客户" variant="borderless">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              customerGender: 1, // 默认男
              elderCount: 1,
              elders: [
                {
                  name: "",
                  gender: 1, // 默认男
                  age: undefined,
                  birthday: undefined,
                  identity: undefined,
                },
              ],
            }}
          >
            {/* 客户基本信息 */}
            <Divider orientation="left">客户基本信息</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="客户姓名"
                  name="customerName"
                  rules={[{ required: true, message: "请输入客户姓名" }]}
                >
                  <Input placeholder="请输入客户姓名" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="客户性别"
                  name="customerGender"
                  rules={[{ required: true, message: "请选择客户性别" }]}
                >
                  <Radio.Group>
                    {genderOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="客户手机号"
                  name="customerPhone"
                  rules={[
                    { required: true, message: "请输入客户手机号" },
                    { pattern: /^1[3-9]\d{9}$/, message: "请输入有效的手机号" },
                  ]}
                >
                  <Input placeholder="请输入客户手机号" />
                </Form.Item>
              </Col>
            </Row>

            {/* 老人资料 */}
            <Divider orientation="left">老人资料</Divider>

            {/* 地址信息（归类到老人信息中） */}
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="省份"
                  name="provinceId"
                  rules={[{ required: true, message: "请选择省份" }]}
                >
                  <Select
                    placeholder="请选择省份"
                    onChange={handleProvinceChange}
                    showSearch
                    optionFilterProp="children"
                  >
                    {provinces.map((province) => (
                      <Option key={province.id} value={province.id}>
                        {province.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="城市"
                  name="cityId"
                  rules={[{ required: true, message: "请选择城市" }]}
                >
                  <Select
                    placeholder="请选择城市"
                    disabled={cities.length === 0}
                    showSearch
                    optionFilterProp="children"
                  >
                    {cities.map((city) => (
                      <Option key={city.id} value={city.id}>
                        {city.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="详细地址"
                  name="detailAddress"
                  rules={[{ required: true, message: "请输入详细地址" }]}
                >
                  <Input placeholder="请输入详细地址" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="纬度"
                  name="latitude"
                  rules={[{ required: true, message: "请输入纬度" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="例如: 31.114019"
                    step={0.000001}
                    precision={6}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="经度"
                  name="longitude"
                  rules={[{ required: true, message: "请输入经度" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="例如: 118.526467"
                    step={0.000001}
                    precision={6}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="老人联系电话"
                  name="elderPhone"
                  rules={[
                    { required: true, message: "请输入老人联系电话" },
                    { pattern: /^1[3-9]\d{9}$/, message: "请输入有效的手机号" },
                  ]}
                >
                  <Input placeholder="请输入老人联系电话" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="老人数量"
                  name="elderCount"
                  rules={[{ required: true, message: "请选择老人数量" }]}
                >
                  <Radio.Group
                    onChange={(e) => handleElderCountChange(e.target.value)}
                  >
                    <Radio value={1}>1位老人</Radio>
                    <Radio value={2}>2位老人</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {/* 动态老人详细信息 */}
            {Array.from({ length: elderCount }).map((_, index) => (
              <Card
                key={index}
                size="small"
                title={`老人${index + 1}信息`}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={5}>
                    <Form.Item
                      label="老人姓名"
                      name={["elders", index, "name"]}
                      rules={[{ required: true, message: "请输入老人姓名" }]}
                    >
                      <Input placeholder="请输入老人姓名" />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      label="与客户关系"
                      name={["elders", index, "identity"]}
                      rules={[{ required: true, message: "请选择与客户关系" }]}
                    >
                      <Select
                        placeholder="请选择与客户关系"
                        onChange={(value) =>
                          handleElderIdentityChange(value, index)
                        }
                      >
                        {elderIdentityOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      label="老人性别"
                      name={["elders", index, "gender"]}
                      rules={[{ required: true, message: "请选择老人性别" }]}
                    >
                      <Radio.Group>
                        {genderOptions.map((option) => (
                          <Radio key={option.value} value={option.value}>
                            {option.label}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="老人出生年月"
                      name={["elders", index, "birthday"]}
                      rules={[
                        { required: true, message: "请选择老人出生年月" },
                      ]}
                    >
                      <DatePicker
                        picker="month"
                        style={{ width: "100%" }}
                        placeholder="请选择出生年月"
                        format="YYYY年MM月"
                        onChange={(date) => handleBirthdayChange(date, index)}
                        defaultPickerValue={dayjs("1960-01-01")}
                        disabledDate={(current) =>
                          current && current > dayjs().endOf("month")
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      label="老人年龄"
                      name={["elders", index, "age"]}
                      rules={[
                        { required: true, message: "请输入或自动计算年龄" },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="自动计算"
                        min={1}
                        max={120}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}

            {/* 订单信息 */}
            <Divider orientation="left">订单信息</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="选择产品"
                  name="productId"
                  rules={[{ required: true, message: "请选择产品" }]}
                >
                  <Select
                    placeholder="请选择产品"
                    showSearch
                    optionFilterProp="children"
                  >
                    {products.map((product) => (
                      <Option key={product.id} value={product.id}>
                        {product.name} - ¥{(product.priceFen / 100).toFixed(2)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* 提交按钮 */}
            <Form.Item>
              <Space size="middle">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                >
                  创建客户订单
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setElderCount(1);
                    setCities([]);
                  }}
                  size="large"
                >
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default CreateCustomer;
